import {useState, useEffect} from 'react';
import styled from '@emotion/styled';
import {colors, fonts} from 'ui/config';
import {isChromeAndroid, isIOS, isIOSSafari} from 'lib/displayMode';
import {
  canPromptInstall,
  isAppInstalled,
  promptInstall,
  subscribeInstallPrompt,
} from 'lib/installPrompt';

// Délai laissé au navigateur pour proposer l'installation avant de conclure qu'il ne le
// fera pas (navigateur sans installation, appli déjà installée).
const PROMPT_WAIT = 4000;

const Screen = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  padding: 'calc(40px + env(safe-area-inset-top)) 28px calc(40px + env(safe-area-inset-bottom))',
  backgroundColor: colors.white,
  textAlign: 'center',
});

const AppIcon = styled('img')({
  width: 72,
  height: 72,
  borderRadius: 14,
  marginBottom: 20,
});

const Title = styled('h1')({
  fontSize: 26,
  fontFamily: fonts.title,
  fontWeight: 'bold',
  color: colors.black,
  margin: '0 0 12px',
});

const Description = styled('p')({
  maxWidth: 420,
  fontSize: 15,
  lineHeight: '23px',
  color: colors.black,
  margin: '0 0 24px',
});

const Instructions = styled('p')({
  maxWidth: 420,
  fontSize: 14,
  lineHeight: '22px',
  color: colors.grey,
  margin: '0 0 24px',
});

const InstallButton = styled('button')({
  width: '100%',
  maxWidth: 420,
  padding: '14px 24px',
  backgroundColor: colors.caramel,
  color: colors.white,
  fontFamily: fonts.title,
  fontWeight: 'bold',
  fontSize: 18,
  border: 'none',
  borderRadius: 0,
  cursor: 'pointer',
});

const AlreadyInstalled = styled('p')({
  maxWidth: 420,
  fontSize: 13,
  lineHeight: '20px',
  color: colors.grey,
  margin: '24px 0 0',
});

const ShareIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke={colors.caramel}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-label="Partager"
    style={{verticalAlign: 'middle', margin: '0 4px'}}>
    <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8" />
    <polyline points="16 6 12 2 8 6" />
    <line x1="12" y1="2" x2="12" y2="15" />
  </svg>
);

const OPEN_FROM_HOME = (
  <>Ouvrez-la depuis l’icône &laquo;&nbsp;Le Club&nbsp;&raquo; de votre écran d’accueil.</>
);

const initialStep = () => {
  if (isIOS()) return isIOSSafari() ? 'ios' : 'ios-other-browser';
  return canPromptInstall() ? 'prompt' : 'waiting';
};

// Dans un navigateur mobile, la PWA ne s'utilise pas : cet écran remplace toute l'application
// (ni connexion ni contenu) et ne propose que de l'installer ou d'ouvrir l'appli installée.
const InstallGate = () => {
  const [step, setStep] = useState(initialStep);

  useEffect(() => {
    if (isIOS()) return () => {};

    let cancelled = false;
    const refresh = async () => {
      if (canPromptInstall()) {
        setStep('prompt');
        return;
      }
      const installed = await isAppInstalled();
      if (cancelled) return;
      setStep(current => {
        if (installed) return 'installed';
        // Une invite refusée ne se rejoue pas : le bouton recharge la page pour en obtenir une.
        return current === 'prompt' || current === 'waiting' ? current : 'other-browser';
      });
    };

    const unsubscribe = subscribeInstallPrompt(refresh);
    const timeout = setTimeout(async () => {
      if (canPromptInstall()) return;
      const installed = await isAppInstalled();
      if (cancelled) return;
      setStep(current => {
        if (current !== 'waiting') return current;
        // Chrome ne propose pas l'installation d'une appli déjà installée : sans invite, c'est
        // qu'elle l'est (getInstalledRelatedApps ne la voit pas toujours).
        return installed || isChromeAndroid() ? 'already-installed' : 'other-browser';
      });
    }, PROMPT_WAIT);

    return () => {
      cancelled = true;
      unsubscribe();
      clearTimeout(timeout);
    };
  }, []);

  const handleInstall = () => {
    if (!canPromptInstall()) {
      window.location.reload();
      return;
    }
    // En cas d'acceptation, « appinstalled » fait passer l'écran à la consigne d'ouverture.
    promptInstall();
  };

  if (step === 'installed' || step === 'already-installed') {
    return (
      <Screen>
        <AppIcon src={`${process.env.PUBLIC_URL}/logo192.png`} alt="" />
        <Title>L’application est {step === 'already-installed' && 'déjà '}installée</Title>
        <Description>{OPEN_FROM_HOME}</Description>
      </Screen>
    );
  }

  return (
    <Screen>
      <AppIcon src={`${process.env.PUBLIC_URL}/logo192.png`} alt="" />
      <Title>Installez l’application</Title>
      <Description>
        Le Club Maison Carte d’Or s’utilise depuis l’application &laquo;&nbsp;Le Club&nbsp;&raquo;, à
        installer sur votre téléphone.
      </Description>

      {step === 'ios' && (
        <Instructions>
          Appuyez sur <ShareIcon /> puis sélectionnez &laquo;&nbsp;Sur l’écran d’accueil&nbsp;&raquo;.
        </Instructions>
      )}

      {step === 'ios-other-browser' && (
        <Instructions>Ouvrez ce lien dans Safari pour installer l’application.</Instructions>
      )}

      {step === 'other-browser' && (
        <Instructions>Ouvrez ce lien dans Chrome pour installer l’application.</Instructions>
      )}

      {step === 'prompt' && (
        <>
          <Instructions>
            Appuyez sur &laquo;&nbsp;Installer&nbsp;&raquo; ci-dessous puis à nouveau sur Installer sur
            l’écran suivant.
          </Instructions>
          <InstallButton type="button" onClick={handleInstall}>
            Installer
          </InstallButton>
        </>
      )}

      {step !== 'prompt' && step !== 'waiting' && (
        <AlreadyInstalled>Application déjà installée&nbsp;? {OPEN_FROM_HOME}</AlreadyInstalled>
      )}
    </Screen>
  );
};

export default InstallGate;
